import { useEffect, useRef, useState } from 'react'

/**
 * The meter hero's count-up (v20, reworked v24). Money ticks UP toward its
 * target with an ease-out tween (each cashout nudges the total and the number
 * rolls to it — the Aviator-feel detail). Pure easing + the per-change plan
 * are exported for tests.
 *
 * Rules that keep it honest and calm:
 *   - counts UP only — a DECREASE within one context (the round reset zeroing
 *     the meter) SNAPS instantly; rolling money backwards reads as a
 *     clawback, which this UI must never imply.
 *   - a CONTEXT change (tab switch, a new previous round) RE-ROLLS the figure
 *     from ZERO up to the new context's total (Kasper note d: switching
 *     All bets ⇄ Previous should count the amount up — the re-roll IS the
 *     tab-switch feedback). Never a tween between the two contexts' values:
 *     that would read as one figure becoming the other.
 *   - prefers-reduced-motion snaps every change.
 */
export function countUpAt(from: number, to: number, progress: number): number {
  const p = Math.min(1, Math.max(0, progress))
  const eased = 1 - Math.pow(1 - p, 3)
  return from + (to - from) * eased
}

export type CountUpPlan = { kind: 'noop' } | { kind: 'snap' } | { kind: 'tween'; from: number }

export function planCountUp(input: {
  contextChanged: boolean
  reducedMotion: boolean
  durationMs: number
  target: number
  shown: number
}): CountUpPlan {
  const degenerate = input.reducedMotion || input.durationMs <= 0
  if (input.contextChanged) {
    // Fresh context: roll up from zero — unless there is nothing to roll to,
    // or motion is off. Even a coincidentally-equal target re-rolls: the
    // motion signals "this is a different figure", not a money change.
    if (degenerate || input.target <= 0) return { kind: 'snap' }
    return { kind: 'tween', from: 0 }
  }
  if (input.shown === input.target) return { kind: 'noop' }
  if (degenerate || input.target < input.shown) return { kind: 'snap' }
  return { kind: 'tween', from: input.shown }
}

export function useCountUp(target: number, durationMs = 450, snapKey = ''): number {
  const [shown, setShown] = useState(target)
  const shownRef = useRef(target)
  shownRef.current = shown
  const snapKeyRef = useRef(snapKey)

  useEffect(() => {
    const contextChanged = snapKeyRef.current !== snapKey
    snapKeyRef.current = snapKey
    const reducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const plan = planCountUp({
      contextChanged,
      reducedMotion,
      durationMs,
      target,
      shown: shownRef.current
    })
    if (plan.kind === 'noop') return undefined
    if (plan.kind === 'snap') {
      setShown(target)
      return undefined
    }
    const { from } = plan
    const start = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const progress = (now - start) / durationMs
      if (progress >= 1) {
        setShown(target)
        return
      }
      setShown(countUpAt(from, target, progress))
      raf = requestAnimationFrame(tick)
    }
    // First frame paints from the plan's origin so a context re-roll visibly
    // starts at zero even if the tween is interrupted immediately.
    setShown(countUpAt(from, target, 0))
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
    // snapKey IS a dependency: a context change with a coincidentally-equal
    // target must still re-run the effect so the re-roll fires and any
    // in-flight tween's rAF is cancelled by cleanup.
  }, [target, durationMs, snapKey])

  return shown
}
